import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Eye, Sparkles, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Logo } from "@/components/Nav";
import ShareButton from "@/components/ShareButton";
import PageView, { CoverView, LAYOUT_LABELS, SLOTS } from "@/components/PageView";
import { PageStrip, PhotoTray } from "@/components/EditorParts";
import { api, errMsg, inr, SIZES, THEMES } from "@/lib/api";

const uid = () => Math.random().toString(36).slice(2, 12);

export default function Editor() {
  const { bookId } = useParams();
  const [book, setBook] = useState(null);
  const [list, setList] = useState([]);
  const [sel, setSel] = useState(0);
  const [swap, setSwap] = useState(null);
  const [saving, setSaving] = useState(false);
  const timer = useRef();

  useEffect(() => {
    api.get(`/books/${bookId}`).then((r) => { setBook(r.data); if (!r.data.pages.length) setSel(-1); });
    api.get(`/books/${bookId}/photos`).then((r) => setList(r.data));
  }, [bookId]);

  const photos = useMemo(() => Object.fromEntries(list.map((p) => [p.id, p])), [list]);
  const used = useMemo(() => new Set((book?.pages || []).flatMap((p) => p.photo_ids)), [book]);

  const persist = useCallback(async (patch) => {
    setSaving(true);
    try { const { data } = await api.patch(`/books/${bookId}`, patch); setBook((b) => ({ ...b, page_count: data.page_count, pricing: data.pricing })); }
    catch (e) { toast.error(errMsg(e)); }
    setSaving(false);
  }, [bookId]);

  const update = (patch, debounce = false) => {
    setBook((b) => ({ ...b, ...patch }));
    clearTimeout(timer.current);
    if (debounce) timer.current = setTimeout(() => persist(patch), 700);
    else persist(patch);
  };
  const setPages = (pages, debounce) => update({ pages }, debounce);
  const editPage = (i, patch, debounce) => setPages(book.pages.map((p, k) => (k === i ? { ...p, ...patch } : p)), debounce);

  const assign = (pid) => {
    if (swap === "cover") { update({ cover_photo_id: pid }); toast.success("Cover updated"); }
    else {
      const pg = book.pages[swap.page];
      const ids = [...pg.photo_ids];
      while (ids.length < swap.slot) ids.push(ids[ids.length - 1]);
      ids[swap.slot] = pid;
      editPage(swap.page, { photo_ids: ids.filter(Boolean) });
    }
    setSwap(null);
  };
  const dropOnSlot = (pageIdx, slot, pid) => {
    if (!pid) return;
    const ids = [...book.pages[pageIdx].photo_ids];
    ids[slot] = pid;
    editPage(pageIdx, { photo_ids: ids.filter(Boolean) });
  };
  const clearSlot = () => { const pg = book.pages[swap.page]; editPage(swap.page, { photo_ids: pg.photo_ids.filter((_, k) => k !== swap.slot) }); setSwap(null); };
  const reorder = (from, to) => { const ps = [...book.pages]; const [m] = ps.splice(from, 1); ps.splice(to, 0, m); setPages(ps); setSel(to); };
  const addPage = () => { setPages([...book.pages, { id: uid(), layout: "two", photo_ids: [], caption: "" }]); setSel(book.pages.length); };
  const delPage = (i) => { setPages(book.pages.filter((_, k) => k !== i)); setSel(Math.max(-1, i - 1)); toast("Page removed — its photos are back in the tray"); };
  const onUploaded = useCallback((acc) => { setList((l) => [...l, ...acc]); if (acc.length) toast.success(`${acc.length} photo(s) added to the tray`); }, []);

  if (!book) return <div className="grid min-h-screen place-items-center font-serif text-2xl text-[#8C8498]">Opening your book…</div>;
  const page = book.pages[sel];
  const locked = book.status === "ordered";

  return (
    <div className="flex min-h-screen flex-col bg-[#F5F0E8]">
      <header className="sticky top-0 z-30 border-b border-[#E8E2D9] bg-[#FDFBF7]/90 backdrop-blur-xl">
        <div className="flex flex-wrap items-center gap-3 px-4 py-3 sm:px-6">
          <div className="hidden md:block"><Logo /></div>
          <Input data-testid="editor-title-input" value={book.title} onChange={(e) => update({ title: e.target.value }, true)} className="h-9 w-full max-w-[260px] border-transparent bg-transparent font-serif text-xl hover:border-[#E8E2D9] focus:bg-white sm:flex-1" />
          <div className="flex flex-1 flex-wrap items-center justify-end gap-2 sm:gap-3">
            <Select value={book.size} onValueChange={(v) => update({ size: v })}>
              <SelectTrigger data-testid="editor-size-select" className="h-9 w-[120px] bg-white text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{SIZES.map((s) => <SelectItem key={s.key} value={s.key} data-testid={`editor-size-${s.key}`}>{s.label} {s.key}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={book.theme} onValueChange={(v) => { update({ theme: v }); toast.success("Theme applied to all pages"); }}>
              <SelectTrigger data-testid="editor-theme-select" className="h-9 w-[140px] bg-white text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{THEMES.map((t) => <SelectItem key={t.key} value={t.key} data-testid={`editor-theme-${t.key}`}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
            <label className="flex items-center gap-2 rounded-full border border-[#E8E2D9] bg-white px-3 py-1.5 text-xs">
              <Sparkles className="h-3.5 w-3.5 text-[#3B5BDB]" />AI enhance
              <Switch data-testid="editor-ai-enhance-global-toggle" checked={book.enhance} onCheckedChange={(v) => { update({ enhance: v }); toast(v ? "Light enhancement on" : "Showing original photos"); }} />
            </label>
            <span className="hidden font-mono text-xs text-[#8C8498] lg:inline" data-testid="editor-page-count">{book.page_count} pp · {inr(book.pricing.total)}{saving ? " · saving…" : " · saved"}</span>
            <ShareButton book={book} onChange={(t) => setBook({ ...book, share_token: t })} />
            <Link to={`/books/${bookId}/preview`} data-testid="print-preview-modal-open-btn" className="btn-ghost !px-4 !py-2"><Eye className="h-4 w-4" />Preview</Link>
            {!locked && <Link to={`/books/${bookId}/checkout`} data-testid="editor-checkout-btn" className="btn-primary !px-4 !py-2">Order</Link>}
          </div>
        </div>
      </header>
      {locked && <p className="bg-amber-50 px-6 py-2 text-center text-xs text-amber-800" data-testid="editor-locked-note">This book has been ordered. Changes here won't affect the printed copy.</p>}

      <div className="grid min-w-0 flex-1 grid-cols-1 gap-0 lg:grid-cols-[150px_minmax(0,1fr)_300px]">
        <aside className="min-w-0 border-b border-[#E8E2D9] bg-[#FDFBF7] p-3 lg:max-h-[calc(100vh-61px)] lg:border-b-0 lg:border-r lg:sticky lg:top-[61px]">
          <PageStrip book={book} photos={photos} sel={sel} setSel={setSel} onReorder={reorder} onAdd={addPage} />
        </aside>

        <main className="flex min-w-0 flex-col items-center px-4 py-8 sm:px-10">
          <div className="w-full max-w-[640px]">
            {sel === -1 ? (
              <>
                <div className="page-shadow"><CoverView book={book} photos={photos} enhance={book.enhance} onClick={() => setSwap("cover")} testid="editor-cover-canvas" /></div>
                <div className="mt-6 space-y-3">
                  <p className="text-center text-xs text-[#8C8498]">Tap the cover to change its photo</p>
                  <Input data-testid="editor-subtitle-input" value={book.subtitle} placeholder="Subtitle" onChange={(e) => update({ subtitle: e.target.value }, true)} className="bg-white" />
                </div>
              </>
            ) : page ? (
              <>
                <div className="page-shadow bg-white">
                  <PageView page={page} photos={photos} size={book.size} theme={book.theme} enhance={book.enhance} testid={`editor-canvas-page-${sel}`}
                    onSlotClick={(slot) => setSwap({ page: sel, slot })} onSlotDrop={(slot, pid) => dropOnSlot(sel, slot, pid)} />
                </div>
                <p className="mt-3 text-center text-xs text-[#8C8498]">Tap a photo to swap it, or drag one in from the tray</p>
                <div className="mt-6 flex flex-wrap gap-2" data-testid="editor-layout-selector-dropdown">
                  {Object.keys(SLOTS).map((l) => (
                    <button key={l} data-testid={`editor-layout-${l}`} onClick={() => editPage(sel, { layout: l, photo_ids: page.photo_ids.slice(0, SLOTS[l].length) })}
                      className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${page.layout === l ? "border-[#26232E] bg-[#1E1B24] text-white" : "border-[#E8E2D9] bg-white hover:border-[#d9cfb6]"}`}>
                      {LAYOUT_LABELS[l]}
                    </button>
                  ))}
                </div>
                <Input data-testid={`editor-caption-input-page-${sel}`} value={page.caption} placeholder="Write a caption…" disabled={page.layout === "full"}
                  onChange={(e) => editPage(sel, { caption: e.target.value }, true)} className="mt-4 bg-white" />
                {page.layout === "full" && <p className="mt-1 text-xs text-[#8C8498]">Full-bleed pages don't show captions.</p>}
                <div className="mt-6 flex items-center justify-between">
                  <div className="flex gap-2">
                    <button data-testid="editor-move-page-left" disabled={sel === 0} onClick={() => reorder(sel, sel - 1)} className="btn-ghost !p-2" aria-label="Move page earlier"><ArrowLeft className="h-4 w-4" /></button>
                    <button data-testid="editor-move-page-right" disabled={sel === book.pages.length - 1} onClick={() => reorder(sel, sel + 1)} className="btn-ghost !p-2" aria-label="Move page later"><ArrowRight className="h-4 w-4" /></button>
                  </div>
                  <button data-testid="editor-delete-page-btn" onClick={() => delPage(sel)} className="flex items-center gap-1.5 text-xs text-[#8C8498] hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" />Remove page</button>
                </div>
              </>
            ) : (
              <p className="text-center text-[#8C8498]">Add a page to get started.</p>
            )}
          </div>
        </main>

        <aside className="border-t border-[#E8E2D9] bg-[#FDFBF7] p-4 lg:max-h-[calc(100vh-61px)] lg:overflow-y-auto lg:border-l lg:border-t-0 lg:sticky lg:top-[61px]">
          <PhotoTray list={list} used={used} enhance={book.enhance} bookId={bookId} onUploaded={onUploaded}
            onPick={(pid) => { if (sel >= 0 && page) { const n = SLOTS[page.layout].length; const ids = page.photo_ids.length < n ? [...page.photo_ids, pid] : [...page.photo_ids.slice(0, n - 1), pid]; editPage(sel, { photo_ids: ids }); } else if (sel === -1) update({ cover_photo_id: pid }); }} />
          <p className="mt-4 text-[11px] leading-relaxed text-[#8C8498]">Tap a tray photo to drop it into the current page. "Set aside" photos were skipped by AI as duplicates or blurry — add them back anytime.</p>
        </aside>
      </div>

      <Sheet open={!!swap} onOpenChange={(o) => !o && setSwap(null)}>
        <SheetContent side="right" className="w-full overflow-y-auto bg-[#FDFBF7] sm:max-w-md" data-testid="swap-photo-sheet">
          <SheetHeader>
            <SheetTitle className="font-serif text-2xl">{swap === "cover" ? "Choose cover photo" : "Swap photo"}</SheetTitle>
            <SheetDescription>Pick any photo from this book.</SheetDescription>
          </SheetHeader>
          {swap && swap !== "cover" && book.pages[swap.page]?.photo_ids[swap.slot] && (
            <button data-testid="swap-clear-slot-btn" onClick={clearSlot} className="btn-ghost mt-4 !py-1.5 !text-xs"><X className="h-3.5 w-3.5" />Leave this spot empty</button>
          )}
          <div className="mt-5"><PhotoTray list={list} used={used} enhance={book.enhance} bookId={bookId} onUploaded={onUploaded} onPick={assign} compact /></div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
